import { History, Loader2, RotateCw, TriangleAlert } from 'lucide-react';

import type { IdeaDetail } from '@/api/types';
import { formatDateTime } from '@/ui/format';
import { StateCard } from '@/ui/StateCard';

import { isPending } from '@/ui/ResearchStatusPill';
import type { IdeaDetailUi } from './useIdeaDetail';

function RunningAlert({ idea }: { idea: IdeaDetail }) {
  return (
    <div className="alert alert-info form-alert" role="status">
      <Loader2 aria-hidden="true" className="spin" />
      <div className="a-body">New research is {idea.research_status === 'queued' ? 'queued' : 'running'}. The scores below are from the previous run until it finishes.</div>
    </div>
  );
}

function FailedAlert({ idea, error, onRerun }: { idea: IdeaDetail; error: string; onRerun: () => void }) {
  return (
    <div className="alert alert-warning form-alert" role="alert">
      <TriangleAlert aria-hidden="true" />
      <div className="a-body">
        <b>The latest research failed.</b> {error}
        {idea.current_research && ' The scores below are from the last successful run.'}
      </div>
      {idea.permissions.can_rerun && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={onRerun}>
          <RotateCw aria-hidden="true" /> Retry
        </button>
      )}
    </div>
  );
}

/** Instead of the strip and report while the idea has no successful research yet. */
function NoResearchCard({ idea, running }: { idea: IdeaDetail; running: boolean }) {
  if (running) {
    return (
      <StateCard icon={<Loader2 aria-hidden="true" className="spin" />} title={idea.research_status === 'queued' ? 'Research is queued' : 'Researching this idea'}>
        <p>AI research is scoring the idea and writing its report. This usually takes a minute or two; the page updates on its own.</p>
      </StateCard>
    );
  }
  if (idea.research_status !== 'failed') return null;
  return (
    <StateCard warning icon={<TriangleAlert aria-hidden="true" />} title="No research yet">
      <p>{idea.permissions.can_rerun ? 'Retry the research once the problem above is solved.' : 'The author or an admin can retry the research.'}</p>
    </StateCard>
  );
}

function ViewingAlert({ ui }: { ui: IdeaDetailUi }) {
  const past = ui.pastRun.data;
  return (
    <div className="alert alert-info form-alert" role="status">
      <History aria-hidden="true" />
      <div className="a-body">You are viewing an earlier run{past ? ` from ${formatDateTime(past.created_at)}` : ''}.</div>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => ui.setViewing(null)}>
        Back to the current run
      </button>
    </div>
  );
}

/** Where research stands: running or failed alerts, the empty state before any research, and the earlier-run banner. */
export function ResearchNotices({ idea, ui }: { idea: IdeaDetail; ui: IdeaDetailUi }) {
  const running = isPending(idea.research_status);
  const failedError = idea.research_status === 'failed' ? idea.latest_run?.error : null;
  const hasResearch = Boolean(idea.current_research);
  return (
    <>
      {running && hasResearch && <RunningAlert idea={idea} />}
      {failedError && <FailedAlert idea={idea} error={failedError} onRerun={() => ui.setConfirm({ kind: 'rerun' })} />}
      {!hasResearch && <NoResearchCard idea={idea} running={running} />}
      {ui.viewing && <ViewingAlert ui={ui} />}
    </>
  );
}
